import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faUpload } from "@fortawesome/free-solid-svg-icons";
import { checkImage, notifyError } from "./adminUtils";

export function BackButton({ to }) {
  return (
    <div className="back-button-holder">
      <Link to={to} className="back-button">
        <FontAwesomeIcon icon={faArrowLeft} /> Back
      </Link>
    </div>
  );
}

export function Switch({ id, checked, onChange, label }) {
  return (
    <label className="dc-switch" htmlFor={id}>
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="dc-switch-track" />
      <span className="dc-switch-label">{label}</span>
    </label>
  );
}

/**
 * Image picker that validates type / size / dimensions on selection and shows a preview
 * (falls back to `currentUrl`, the image that is already saved).
 */
export function ImagePicker({ label, spec, currentUrl, onChange, resetKey }) {
  const [preview, setPreview] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    return () => preview && URL.revokeObjectURL(preview);
  }, [preview]);

  // the parent bumps resetKey to clear the selection (modal closed / saved)
  useEffect(() => {
    setPreview("");
    if (inputRef.current) inputRef.current.value = "";
  }, [resetKey]);

  const handle = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const error = await checkImage(file, spec);
    if (error) {
      e.target.value = "";
      notifyError(error);
      return;
    }
    setPreview(URL.createObjectURL(file));
    onChange(file);
  };

  const shown = preview || currentUrl;
  return (
    <div className="dc-picker">
      <label>
        {label} ({spec.width}x{spec.height})
      </label>
      <div className="dc-picker-box">
        {shown ? <img src={shown} alt="" /> : <FontAwesomeIcon icon={faUpload} />}
        <input
          ref={inputRef}
          type="file"
          accept={spec.exts.map((e) => (e === "jpg" ? "image/jpeg" : `image/${e}`)).join(",")}
          onChange={handle}
        />
      </div>
    </div>
  );
}

export function Modal({ open, onClose, children }) {
  if (!open) return null;
  return (
    <div className="dc-modal-backdrop" onMouseDown={onClose}>
      <div className="dc-modal" onMouseDown={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

/** A file input + current preview + Upload button used by the "Add Content" cards. */
export function UploadCard({ title, notes, spec, currentUrl, previewWidth, onUpload }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [localPreview, setLocalPreview] = useState("");
  const inputRef = useRef(null);

  // preview of the picked file (before it is uploaded)
  useEffect(() => {
    if (!file) return setLocalPreview("");
    const url = URL.createObjectURL(file);
    setLocalPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const pick = async (e) => {
    const picked = e.target.files[0] || null;
    if (picked) {
      const error = await checkImage(picked, spec);
      if (error) {
        e.target.value = "";
        setFile(null);
        return notifyError(error);
      }
    }
    setFile(picked);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return notifyError("Please select a file");
    const error = await checkImage(file, spec);
    if (error) return notifyError(error);
    setBusy(true);
    try {
      await onUpload(file);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dc-card dc-upload-card">
      <h4 className="dc-card-title">{title}</h4>
      <form onSubmit={submit}>
        <input
          ref={inputRef}
          type="file"
          className="dc-file"
          accept={spec.exts.map((x) => (x === "jpg" ? "image/jpeg" : `image/${x}`)).join(",")}
          onChange={pick}
        />
        <p className="dc-note">
          {notes.map((n) => (
            <code key={n}>* {n}</code>
          ))}
        </p>
        <button type="submit" className="dc-btn dc-btn-primary" disabled={busy}>
          {busy ? "Uploading..." : "Upload"}
        </button>
      </form>
      <h4 className="dc-preview-title">Preview</h4>
      <div className="dc-preview">{localPreview || currentUrl ? <img src={localPreview || currentUrl} alt="" style={{ maxWidth: previewWidth }} /> : <em>No image</em>}</div>
    </div>
  );
}
